import * as yup from 'yup';

import { webhooksEndpoints } from '@/api/endpoints';
import { WebhookType, type Webhook } from '@/shared/types/webhook';
import { LENGTH_L, LENGTH_S, LENGTH_XL, LENGTH_XXL } from '@/util/validation/constants';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

import { formatTestPayload } from './components/webhookJsonUtils';

export interface WebhookWizardHeader {
  name: string;
  value: string;
}

export interface WebhookWizardFormValues {
  type: Webhook['type'];
  name: string;
  url: string;
  token: string;
  includeHeaders: boolean;
  headers: WebhookWizardHeader[];
  testPayload: string;
}

export const getDefaultWebhookWizardValues = (
  type: WebhookType = WebhookType.ACTION
): WebhookWizardFormValues => ({
  type,
  name: '',
  url: '',
  token: '',
  includeHeaders: false,
  headers: [{ name: '', value: '' }],
  testPayload: formatTestPayload(type),
});

/**
 * Creates validation schema for the webhook wizard flow with duplicate name checking.
 * @param brainId - The brain ID to check for duplicate webhook names
 * @param webhookId - Optional webhook ID to exclude from duplicate check (for edit mode)
 * @param currentName - Optional current webhook name to exclude from validation (for edit mode)
 * @returns Yup schema for webhook validation
 */
export const createWebhookWizardSchema = (
  brainId: string,
  webhookId?: string,
  currentName?: string
) => {
  const parentParams = webhookId ? { webhook_id: webhookId } : undefined;

  return yup
    .object()
    .shape({
      name: createNameFieldSchema({
        getEndpoint: (name) => webhooksEndpoints.checkName(brainId, name, parentParams),
        max: LENGTH_S,
        excludeName: currentName,
      }),
      url: yup.string().url().max(LENGTH_XXL).required().defined(),
      token: yup.string().max(LENGTH_XL).required().defined(),
      type: yup.string().oneOf(Object.values(WebhookType)).required().defined(),
      includeHeaders: yup.boolean().default(false),
      headers: yup
        .array()
        .of(
          yup
            .object()
            .shape({
              name: yup.string().trim().max(LENGTH_L).default(''),
              value: yup.string().default(''),
            })
        )
        .default([{ name: '', value: '' }]),
      testPayload: yup.string().default(formatTestPayload()),
    })
    .required();
};
